import { useAltSummOverride } from 'contexts/altsumm-override/AltSummOverrideContext';
import { PublicationSetting } from 'data/generated/graphql';
import { getDomain } from 'hooks/altsumm/altSummDomain';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent, AllessehIframeRelatedLink } from 'hooks/useAllessehContentQuery';
import { SnippetyResponseWithChart, SnippetyResponseWithVideo, useSnippetyQuery } from 'hooks/useSnippetyQuery';
import { createChartUrl } from 'utils/chart';
import { decodeHtmlEntities, encodeHtmlEntities } from 'utils/text';
import { formatBulletLink } from 'utils/url';

import { GUID_PATTERN } from 'components/altsumm-modal/components/media-input/MediaInput.utils';

export type AltSummBullet = { text: string; link: string };

export type AltSummFields = {
  flashline: string;
  headline: string;
  body: string;
  image: string;
  imageCredit: string;
  chart: string;
  video: string;
  videoImage: string;
  videoUrl: string;
  bullets: string[];
  bulletsStructured: AltSummBullet[];
  subhead: string;
  chartlosId: string;
  iframe: IframeAltSumm;
};

export type AltSummVariant =
  'U.S. Home' | 'ITP' | 'NewsPlus' | 'APIImage' | 'Mobile' | 'SEO' | 'Social Open Graph' | 'Home-Page' | 'Images';

export const createVideoUrl = (videoGuid: string, publicationSetting?: PublicationSetting) => {
  if (!GUID_PATTERN.test(videoGuid)) {
    return '';
  }
  return `${
    publicationSetting?.snippetyVideoDomain ?? 'https://video-api.wsj.com/api-video/player/v3/iframe.html?guid='
  }${videoGuid}`;
};

export const getAltSummForVariant = (content: AllessehContent, altSummVariant: AltSummVariant) =>
  content.data.attributes.alt_summaries?.find((altSummary: { variant?: string }) => {
    const variant = altSummVariant !== 'Images' ? `|${altSummVariant}|` : altSummVariant;
    return altSummary.variant === variant;
  });

export const getDefaultFlashline = (content: AllessehContent, altSummVariant: AltSummVariant) => {
  const altSummary = getAltSummForVariant(content, altSummVariant);
  const paragraphObjects = altSummary?.content?.filter((node) => node.type === 'paragraph');
  const mainTextContentObject = paragraphObjects?.find(
    (paragraphObject) =>
      Array.isArray(paragraphObject.content) &&
      paragraphObject.content.length > 0 &&
      paragraphObject.content.find((textContent) => textContent.type === 'flashline')
  );
  const mainFlashlineObject = mainTextContentObject?.content?.find((node) => node.type === 'flashline');

  return mainFlashlineObject?.content?.[0]?.text ?? '';
};

export const getDefaultHeadline = (content: AllessehContent, altSummVariant: AltSummVariant) => {
  const altSummary = getAltSummForVariant(content, altSummVariant);
  const paragraphObjects = altSummary?.content?.filter((content) => content.type === 'paragraph');
  const mainTextContentObject = paragraphObjects?.find(
    (paragraphObject) =>
      Array.isArray(paragraphObject.content) &&
      paragraphObject.content.length > 0 &&
      paragraphObject.content.find((textContent) => textContent.type === 'headline')
  );
  const mainHeadlineObject = mainTextContentObject?.content?.find((content) => content.type === 'headline');
  return mainHeadlineObject?.content?.[0].text ?? content.data.attributes.headline?.text ?? '';
};

export const getDefaultBody = (content: AllessehContent, altSummVariant: AltSummVariant) => {
  const altSummary = getAltSummForVariant(content, altSummVariant);
  const paragraphObjects = altSummary?.content?.filter((content) => content.type === 'paragraph');
  const mainParagraphContentObject = paragraphObjects?.find(
    (paragraphObject) =>
      Array.isArray(paragraphObject.content) &&
      paragraphObject.content.length === 1 &&
      paragraphObject.content.find((textContent) => !textContent.type && textContent.text)
  );
  return mainParagraphContentObject?.content?.[0]?.text ?? content.data.attributes.standfirst?.content?.[0].text ?? '';
};

export const getDefaultSubhead = (content: AllessehContent, altSummVariant: AltSummVariant) => {
  const altSummary = getAltSummForVariant(content, altSummVariant);
  const subheadObject = altSummary?.content?.find(
    (content) => content.type === 'hed' && content.hed_type === 'subhead'
  );
  return subheadObject?.content?.[0].text ?? '';
};

export const getDefaultImage = (content: AllessehContent, altSummVariant: AltSummVariant) => {
  const altSummary = getAltSummForVariant(content, altSummVariant);
  const summaryImageRef = altSummary?.content?.find((content) => content.type === 'image')?.ref;

  const summaryImageUrl = content.links?.related?.find((link) => link.id === summaryImageRef)?.properties?.location;
  const defaultSummaryImage = getAltSummForVariant(content, 'Images');
  const defaultSummaryImageRef = defaultSummaryImage?.content?.find((content) => content.type === 'image')?.ref;
  const defaultSummaryImageUrl = content.links?.related?.find((link) => link.id === defaultSummaryImageRef)?.properties
    ?.location;
  const fallbackAltSummImageUrl =
    content.links?.related?.find((link) => link.type === 'image')?.properties?.location ?? '';

  return summaryImageUrl ?? defaultSummaryImageUrl ?? fallbackAltSummImageUrl;
};

export const getDefaultImageCredit = (content: AllessehContent, altSummVariant: AltSummVariant) => {
  const altSummary = getAltSummForVariant(content, altSummVariant);
  const summaryImageRef = altSummary?.content?.find((content) => content.type === 'image')?.ref;
  const summaryImageCredit = content.links?.related?.find((link) => link.id === summaryImageRef)?.credit;

  const defaultSummaryImage = getAltSummForVariant(content, 'Images');
  const defaultSummaryImageRef = defaultSummaryImage?.content?.find((content) => content.type === 'image')?.ref;
  const defaultSummaryImageCredit = content.links?.related?.find((link) => link.id === defaultSummaryImageRef)?.credit;
  const fallbackAltSummImageCredit = content.links?.related?.find((link) => link.type === 'image')?.credit ?? '';

  return summaryImageCredit ?? defaultSummaryImageCredit ?? fallbackAltSummImageCredit;
};

export const getVideoLink = (content: AllessehContent, altSummVariant: AltSummVariant) => {
  const altSummary = getAltSummForVariant(content, altSummVariant);
  const containsAltSummaryWithVideo = !!altSummary?.content?.find((content) => content.type === 'video');
  return containsAltSummaryWithVideo
    ? content.links?.related?.find((linkData) => linkData.type === 'video')
    : undefined;
};

export const getDefaultVideoGuid = (content: AllessehContent, altSummVariant: AltSummVariant) => {
  const videoLink = getVideoLink(content, altSummVariant);
  return videoLink?.name ?? '';
};

export const getDefaultVideo = (
  content: AllessehContent,
  altSummVariant: AltSummVariant,
  publicationSettings?: PublicationSetting
) => {
  const guid = getVideoLink(content, altSummVariant)?.name ?? '';

  return publicationSettings?.snippetyVideoDomain && guid ? createVideoUrl(guid, publicationSettings) : '';
};

export const getDefaultBulletsStructured = (
  content: AllessehContent,
  altSummVariant: AltSummVariant
): AltSummBullet[] => {
  const altSummary = getAltSummForVariant(content, altSummVariant);
  const bulletListObject = altSummary?.content?.find((c) => c.type === 'list');
  const bulletItems = bulletListObject?.content ?? [];
  const relatedLinks = content.links?.related ?? [];

  return bulletItems.map((bulletContent) => {
    const ref = bulletContent.content?.[0]?.ref;
    const text = bulletContent.content?.[0]?.text ?? '';
    const link = relatedLinks.find((l) => l.id === ref)?.uri ?? '';
    return { text, link };
  });
};

export const getDefaultBullets = (content: AllessehContent, altSummVariant: AltSummVariant): string[] =>
  getDefaultBulletsStructured(content, altSummVariant).map(({ text, link }) =>
    formatBulletLink(encodeHtmlEntities(link), encodeHtmlEntities(text))
  );

export const getDefaultChartId = (content: AllessehContent, altSummVariant: AltSummVariant) => {
  const altSummary = getAltSummForVariant(content, altSummVariant);
  const containsAltSummaryWithChart = !!altSummary?.content?.find(
    (content) => content.type === 'media' && content.ref?.startsWith('media')
  );
  const chartLink = content.links?.related?.find(
    (linkData) => linkData.type === 'media' && linkData.media_type?.includes('chart')
  );
  return (containsAltSummaryWithChart && chartLink?.name) || '';
};

export const getDefaultChartMedia = (content: AllessehContent, altSummVariant: AltSummVariant) => {
  const altSummary = getAltSummForVariant(content, altSummVariant);
  const chartAltSummObject = altSummary?.content?.find(
    (content) => content.type === 'media' && content.ref?.startsWith('media')
  );
  const media = chartAltSummObject?.properties?.fallback_image?.display_variants.narrow.url;
  if (media) {
    return media;
  }
  const chartLink = content.links?.related?.find(
    (linkData) => linkData.type === 'media' && linkData.media_type?.includes('chart')
  );
  return chartLink?.properties?.fallback_image?.display_variants.narrow.url ?? '';
};

export const EMPTY_IFRAME: IframeAltSumm = {
  type: 'inset',
  inset_type: 'IFRAME',
  properties: { url: '', fallback: { type: 'image', url: '', link_url: '' } }
};

export const getDefaultIframeImage = (content: AllessehContent, altSummVariant: AltSummVariant): IframeAltSumm => {
  const altSummary = getAltSummForVariant(content, altSummVariant);
  const containsAltSummaryWithIframe = altSummary?.content?.find(
    (c) => c.type === 'inset' && c.inset_type === 'iframe'
  );
  const iframeRef = containsAltSummaryWithIframe?.ref;

  if (containsAltSummaryWithIframe) {
    const iframeData = content.links?.related?.find(
      (linkData): linkData is AllessehIframeRelatedLink =>
        linkData.id === iframeRef && linkData.type === 'inset' && linkData.inset_type === 'iframe'
    );
    if (!iframeData) return EMPTY_IFRAME;
    const props = iframeData.properties;
    return {
      type: 'inset',
      inset_type: 'IFRAME',
      properties: {
        url: props?.url ?? '',
        fallback: props?.fallback ?? { type: 'image', url: '', link_url: '' }
      }
    };
  }
  return EMPTY_IFRAME;
};

export const useAltSummFields = (content: AllessehContent, altSummVariant: AltSummVariant): AltSummFields => {
  const { data: publicationSettingsResp } = usePublicationSettings();
  const publicationSettings = publicationSettingsResp?.publicationSetting;

  const contentId = content.data.id;
  const override = useAltSummOverride(contentId, altSummVariant);

  const guid = getDefaultVideoGuid(content, altSummVariant);
  const video = getDefaultVideo(content, altSummVariant, publicationSettings);
  const videoSnippetyUrl = getDomain(publicationSettings, 'video', guid);
  const { data: videoData } = useSnippetyQuery(videoSnippetyUrl, { enabled: !!videoSnippetyUrl });
  const snippetyVideo = videoData as SnippetyResponseWithVideo | null | undefined;
  const videoImage = !snippetyVideo?.error && snippetyVideo?.media ? snippetyVideo.media : '';

  const chartlosId = getDefaultChartId(content, altSummVariant);
  const chartSnippetyUrl = getDomain(publicationSettings, 'chart', chartlosId);
  const { data: chartData } = useSnippetyQuery(chartSnippetyUrl, { enabled: !!chartSnippetyUrl });
  const snippetyChart = chartData as SnippetyResponseWithChart | null | undefined;
  const chart =
    !snippetyChart?.error && snippetyChart && !('error' in snippetyChart)
      ? createChartUrl(snippetyChart)
      : getDefaultChartMedia(content, altSummVariant);

  const iframe = getDefaultIframeImage(content, altSummVariant);

  let image = getDefaultImage(content, altSummVariant);
  if (video || chart || iframe.properties.url) {
    image = '';
  }

  const fields = {
    flashline: decodeHtmlEntities(getDefaultFlashline(content, altSummVariant)),
    headline: decodeHtmlEntities(getDefaultHeadline(content, altSummVariant)),
    body: decodeHtmlEntities(getDefaultBody(content, altSummVariant)),
    image,
    imageCredit: getDefaultImageCredit(content, altSummVariant),
    video,
    videoUrl: video,
    videoImage,
    chart,
    chartlosId,
    bullets: getDefaultBullets(content, altSummVariant).map(decodeHtmlEntities),
    bulletsStructured: getDefaultBulletsStructured(content, altSummVariant),
    subhead: getDefaultSubhead(content, altSummVariant),
    iframe
  };

  if (override) return override;

  return fields;
};
